import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { RotaSummary } from '../../../classes/models/rota/RotaSummary';
import { map } from 'rxjs/operators';
import { Rota } from '../../../classes/models/rota/Rota';
import { RotaSummaryAdapter } from '../../../classes/adapters/response/rota/RotaSummaryAdapter';
import { RotaAdapter } from '../../../classes/adapters/response/rota/RotaAdapter';
import { RotaErrorsResponse, RotaResponse, RotaSummaryResponse } from '../../../interfaces/response/RotaResponses';
import { RotaErrors } from '../../../classes/models/rota/RotaErrors';
import { RotaErrorsAdapter } from '../../../classes/adapters/response/rota/RotaErrorsAdapter';

/**
 * This service communicates with backend to retrieve rotas.
 */
@Injectable({
  providedIn: 'root'
})
export class RotaService {
  private http = inject(HttpClient);
  private rotaSummaryAdapter = inject(RotaSummaryAdapter);
  private rotaAdapter = inject(RotaAdapter);
  private rotaErrorsAdapter = inject(RotaErrorsAdapter);

  url = environment.apiUrl + '/rotas';

  /**
   * Retrieves a list of rota summaries for the given store, within the given date range.
   * @param store an identifier of a store.
   * @param fromWeek the first week of rota; must be Monday.
   * @param toWeek the last week of rota; must be Monday.
   */
  getRotaSummaries(store: string, fromWeek: string, toWeek: string): Observable<RotaSummary[]> {
    const params = { params: { store, fromWeek, toWeek } };
    return this.http.get(this.url, params).pipe(
      map((data: RotaSummaryResponse[]) => data.map(item => this.rotaSummaryAdapter.adapt(item))));
  }

  /**
   * Retrieves the full information of the specific rota.
   * @param storeId an identifier of a store.
   * @param date the start date of a rota.
   */
  getRota(storeId: string, date: string): Observable<Rota> {
    const url = `${this.url}/${storeId}/${date}`;
    return this.http.get(url).pipe(
      map((item: RotaResponse) => this.rotaAdapter.adapt(item)));
  }

  /**
   * @deprecated Use {@link getStructuredStoreErrors} instead. Structured store errors should be displayed in a
   * human-readable text and provide links for users to fix those errors.
   *
   * Retrieves a list of all errors for the specific store.
   * @param id an identifier of a store.
   */
  getStoreErrors(id: string): Observable<string[]> {
    const url = `${this.url}/${id}/errors`;
    return this.http.get<string[]>(url);
  }

  /**
   * Use this method get structured errors, display them in a human-readable text and provide links for users to fix those errors.
   *
   * Retrieves a list of all structured errors for the specific store.
   * @param id an identifier of a store.
   */
  getStructuredStoreErrors(id: string): Observable<RotaErrors> {
    const url = `${this.url}/${id}/structured-errors`;
    return this.http.get(url).pipe(
      map((item: RotaErrorsResponse) => this.rotaErrorsAdapter.adapt(item)));
  }
}
